import { autoElement } from '../../../webmodule-common/components/src/webmodule-components';
import {
  BaseLoginModalEditProperties,
  ILicenseServerUIStaffPageProvider,
  ILoginClaimManager,
  IStaffPage,
  licenseServerUIBootLoader,
  ViewBaseLogin
} from '../data/licenseserver-integration';
import { claimIdentity, userSecurity } from '../../../webmodule-common/other/api/user-security';
import { currentUserClaims } from '../../../webmodule-common/other/currentuser-claims';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { getCredentials } from '../../api/licenseserver-credentials';
import { getTerritoryManager } from '../data/territory-manager';
import { html } from 'lit';
import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { PromiseTemplate, Snippet } from '../../../webmodule-common/interop/webmodule-interop';
import { repeat } from 'lit/directives/repeat.js';
import { tlang } from '../../../webmodule-common/other/language/lang';
import { validIdOrEmpty } from '../../../webmodule-common/other/ui/string-helper-functions';

@autoElement()
export class SettingsStaffView extends PageControlTabWithIndependantSaving {
  private staffPageProvider: ILicenseServerUIStaffPageProvider;
  private userConsole?: IStaffPage;
  private territoryManager = getTerritoryManager();

  constructor() {
    super();
    this.pageFragment = 'staff';

    this.staffPageProvider = this.staffPageProviderFactory();

    console.log(JSON.stringify(this.staffPageProvider.getColumnProperties?.()));
  }

  public async afterConstruction(): Promise<void> {
    const licUI = await licenseServerUIBootLoader();
    await this.territoryManager.needsTerritories();
    this.userConsole = licUI?.getStaffPage(this.staffPageProvider);
  }

  public allowDeletePage(): boolean {
    return false;
  }

  async prepareForSave(): Promise<void> {
    //
  }

  public internalDataChanged(): boolean {
    return false;
  }

  async onEnter(): Promise<void> {
    this.requestUpdate();
  }

  public buttonMenu(): Snippet {
    const isAdmin = currentUserClaims().isAdmin;
    const buttons = this.userConsole?.actions().map(x => {
      return html` <button class="btn btn-primary" ?disabled=${!x.enabled || !isAdmin} @click=${x.event}>
        ${x.label}
      </button>`;
    });
    return html`${buttons}`;
  }

  protected getCaption(): Snippet {
    return tlang`Staff`;
  }

  protected async internalSaveData(): Promise<boolean> {
    //nothing to do
    return true;
  }
  get isAdmin(): boolean {
    return userSecurity().claimIsTrue(claimIdentity.admin);
  }

  protected async bodyTemplate(): PromiseTemplate {
    return this.userConsole?.template() ?? html``;
  }

  buildModalProperties(): BaseLoginModalEditProperties[] {
    const results: BaseLoginModalEditProperties[] = [];
    if (this.isAdmin) results.push(this.buildPowerUserProperty());
    if (this.isAdmin) results.push(this.buildDealerTerritoryProperty());
    if (this.isAdmin) results.push(this.buildAllowEditSettingsProperty());
    if (this.isAdmin) results.push(this.buildAssignTerritoriesProperty());

    return results;
  }
  buildDealerTerritoryProperty(): BaseLoginModalEditProperties {
    let dealerTerritoryId: string | undefined;
    const changeEvent = (e: Event) => (dealerTerritoryId = (e.currentTarget as HTMLSelectElement).value);
    const allTerritories = this.territoryManager.territories;
    const frameConfigEditor: BaseLoginModalEditProperties = {
      inputId: 'dealerTerritory',
      visible: this.isAdmin,
      editControl: (claimManager: ILoginClaimManager) => {
        if (!dealerTerritoryId)
          dealerTerritoryId = claimManager.getClaim(claimIdentity.supplierDealerTerritory) ?? emptyGuid;
        return html`<select @change=${changeEvent} id="dealerTerritory" class="form-select">
          <option ?selected=${dealerTerritoryId === emptyGuid} value=${emptyGuid}>
            ${tlang`All %%franchisee%% Territories`}
          </option>
          ${repeat(
            allTerritories,
            item => item.id,
            item => html`<option ?selected=${dealerTerritoryId === item.id} value=${item.id}>${item.name}</option>`
          )}
        </select>`;
      },
      label: tlang`%%Franchisee%% Territory`,
      propertyId: 1,
      save: (claimManager: ILoginClaimManager) => {
        claimManager.setClaim(claimIdentity.supplierDealerTerritory, validIdOrEmpty(dealerTerritoryId));
      }
    };
    return frameConfigEditor;
  }
  buildPowerUserProperty(): BaseLoginModalEditProperties {
    let powerUserValue: string | undefined;
    const changeEvent = (e: Event) => (powerUserValue = (e.currentTarget as HTMLSelectElement).value);

    const frameConfigEditor: BaseLoginModalEditProperties = {
      inputId: 'powerUser',
      visible: this.isAdmin,
      editControl: (claimManager: ILoginClaimManager) => {
        if (!powerUserValue) powerUserValue = claimManager.getClaim(claimIdentity.frameConfigPowerUser) ?? 'false';
        return html`<select @change=${changeEvent} id="powerUser" class="form-select">
          <option ?selected=${powerUserValue === 'false'} value=${'false'}>
            ${tlang`%%franchisee%% Standard %%frame%% Configurator`}
          </option>
          <option ?selected=${powerUserValue === 'true'} value=${'true'}>
            ${tlang`%%franchisee%% Power User %%frame%% Configurator`}
          </option>
        </select>`;
      },
      label: tlang`%%frame%% Power User`,
      propertyId: 1,
      save: (claimManager: ILoginClaimManager) => {
        claimManager.setClaim(claimIdentity.frameConfigPowerUser, powerUserValue ?? 'false');
      }
    };
    return frameConfigEditor;
  }

  buildAllowEditSettingsProperty(): BaseLoginModalEditProperties {
    let allowEditSettingsValue: boolean | undefined;
    const changeEvent = (e: Event) => (allowEditSettingsValue = (e.currentTarget as HTMLInputElement).checked);

    const allowEditSettingsEditor: BaseLoginModalEditProperties = {
      inputId: 'allowEditSettings',
      visible: this.isAdmin,
      editControl: (claimManager: ILoginClaimManager) => {
        if (allowEditSettingsValue === undefined)
          allowEditSettingsValue = claimManager.getClaim(claimIdentity.supplierAllowEditSettings) === 'true';
        return html`<input
          type="checkbox"
          @change=${changeEvent}
          id="allowEditSettings"
          class="form-check-input"
          ?checked=${allowEditSettingsValue}
        />`;
      },
      label: tlang`Allow Edit Settings`,
      propertyId: 2,
      save: (claimManager: ILoginClaimManager) => {
        claimManager.setClaim(claimIdentity.supplierAllowEditSettings, allowEditSettingsValue ? 'true' : 'false');
      }
    };
    return allowEditSettingsEditor;
  }

  buildAssignTerritoriesProperty(): BaseLoginModalEditProperties {
    let assignTerritoriesValue: boolean | undefined;
    const changeEvent = (e: Event) => (assignTerritoriesValue = (e.currentTarget as HTMLInputElement).checked);

    const assignTerritoriesEditor: BaseLoginModalEditProperties = {
      inputId: 'assignTerritories',
      visible: this.isAdmin,
      editControl: (claimManager: ILoginClaimManager) => {
        if (assignTerritoriesValue === undefined)
          assignTerritoriesValue = claimManager.getClaim(claimIdentity.supplierAssignTerritories) === 'true';
        return html`<input
          type="checkbox"
          @change=${changeEvent}
          id="assignTerritories"
          class="form-check-input"
          ?checked=${assignTerritoriesValue}
        />`;
      },
      label: tlang`Assign Territories`,
      propertyId: 3,
      save: (claimManager: ILoginClaimManager) => {
        claimManager.setClaim(claimIdentity.supplierAssignTerritories, assignTerritoriesValue ? 'true' : 'false');
      }
    };
    return assignTerritoriesEditor;
  }

  protected staffPageProviderFactory(): ILicenseServerUIStaffPageProvider {
    return {
      credentials: getCredentials(),
      onAfterSave: async (login: ViewBaseLogin) => {
        console.log(`login saved ${login.baseLogin.userName}`);
      },
      onBeforeSave: async (login: ViewBaseLogin) => {
        console.log(`login saving ${login.baseLogin.userName}`);
        return true;
      },
      canEditUser: (_viewLogin: ViewBaseLogin): boolean => {
        //full admin can edit anyone
        const claims = currentUserClaims();
        if (claims.isAdmin) return true;
        //if admin, and only admin, we can see everything.
        return false;
      },
      getColumnProperties: () => {
        if (this.userConsole?.supportsRoles) return [];
        return [
          {
            displayValue: (login: ViewBaseLogin) => {
              const isPowerUser = login.deploymentClaims[claimIdentity.frameConfigPowerUser] === 'true';
              const canAssignTerritories = login.deploymentClaims[claimIdentity.supplierAssignTerritories] === 'true';
              const canEditSettings = login.deploymentClaims[claimIdentity.supplierAllowEditSettings] === 'true';

              return html`
                <div class="d-flex gap-1 flex-wrap">
                  ${isPowerUser
                    ? html`<span class="badge bg-primary badge-sm" style="font-size: 0.7em;"
                        >${tlang`Power User`}</span
                      >`
                    : ''}
                  ${canAssignTerritories
                    ? html`<span class="badge bg-primary badge-sm" style="font-size: 0.7em;"
                        >${tlang`Territories`}</span
                      >`
                    : ''}
                  ${canEditSettings
                    ? html`<span class="badge bg-primary badge-sm" style="font-size: 0.7em;">${tlang`Settings`}</span>`
                    : ''}
                </div>
              `;
            },
            title: () => tlang`Permissions`
          }
        ];
      },
      getExtendedRoles: (login: ViewBaseLogin) => {
        const roles: string[] = [];
        if (login.deploymentClaims[claimIdentity.frameConfigPowerUser] === 'true') roles.push(tlang`Power User`);
        if (login.deploymentClaims[claimIdentity.supplierAssignTerritories] === 'true') roles.push(tlang`Territories`);
        if (login.deploymentClaims[claimIdentity.supplierAllowEditSettings] === 'true') roles.push(tlang`Settings`);
        return roles;
      },
      getModalProperties: () => this.buildModalProperties(),
      uiChanged: () => this.requestUpdate()
    };
  }
}
